import {
  getWorkspaceImageFromStorage,
  uploadWorkspaceImage,
} from "@/lib/db/utils/supabase/storage/workspaceImages";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { Camera, Upload, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

/**
 * Team logo upload component for workspace creation
 */
export default function TeamLogoUpload({
  onImageChange,
  existingImagePath = null,
  workspaceId = null,
}) {
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    // Get the current authenticated user
    const getUserInfo = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setCurrentUser(user);
    };

    getUserInfo();
  }, []);

  useEffect(() => {
    // If there's an existing image path, fetch and display it
    if (existingImagePath) {
      const fetchExistingImage = async () => {
        try {
          // Check if the path appears to be in the expected format
          const expectedFormatRegex = /^[^\/]+\/workspaces\/[^\/]+\/\d+$/; // eslint-disable-line no-useless-escape
          const isExpectedFormat =
            typeof existingImagePath === "string" &&
            expectedFormatRegex.test(existingImagePath);

          // Try using direct URL first for speed
          const directUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/avatars/${existingImagePath}`;

          // Set the preview to the direct URL
          setPreview(directUrl);

          // Start the async signed URL process in parallel for better security/performance
          getWorkspaceImageFromStorage(existingImagePath)
            .then((signedUrl) => {
              if (signedUrl) {
                setPreview(signedUrl);
              }
            })
            .catch((err) => {
              console.error("Error getting signed URL:", err);
              // If signed URL fails but we still have the direct URL, we're ok
            });
        } catch (error) {
          console.error("Error in fetchExistingImage:", error);
          setError("Could not load image");
        }
      };

      fetchExistingImage();
    } else {
      setPreview(null);
    }
  }, [existingImagePath, workspaceId]);

  // Function to get workspace members
  const getWorkspaceMembers = async (workspaceId) => {
    try {
      const { data: workspace, error } = await supabase
        .from("workspaces")
        .select("workspace_members, owner_id")
        .eq("id", workspaceId)
        .single();

      if (error) throw error;

      // Combine owner and members into a single array of user IDs
      const members = workspace.workspace_members || [];
      if (workspace.owner_id && !members.includes(workspace.owner_id)) {
        members.push(workspace.owner_id);
      }

      return members;
    } catch (error) {
      console.error("Error fetching workspace members:", error);
      return [];
    }
  };

  // Function to duplicate the image to member paths
  const duplicateImageToMemberPaths = async (file, originalPath, members) => {
    try {
      const promises = [];

      // Extract the filename from the original path
      const pathParts = originalPath.split("/");
      const filename = pathParts[pathParts.length - 1];
      const workspaceId = pathParts[pathParts.length - 2]; // Get the workspace ID from the path

      for (const memberId of members) {
        // Skip if it's the current user (already has the image)
        if (memberId === currentUser?.id) continue;

        // Create the member-specific path
        const memberPath = `${memberId}/workspaces/${workspaceId}/${filename}`;

        // Create a copy of the file in the member's path
        const { data, error } = await supabase.storage
          .from("avatars")
          .copy(originalPath, memberPath);

        if (error) {
          console.error(`Error copying to member ${memberId}:`, error);
        }

        promises.push({
          memberId,
          path: memberPath,
          result: error ? "error" : "success",
        });
      }

      return promises;
    } catch (error) {
      console.error("Error duplicating images to member paths:", error);
      return [];
    }
  };

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setError(null);

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      // Validate file size (max 3MB)
      if (file.size > 3 * 1024 * 1024) {
        setError("Image must be less than 3MB");
        return;
      }

      setIsUploading(true);

      try {
        // Create object URL for preview
        const previewUrl = URL.createObjectURL(file);
        setPreview(previewUrl);

        // Upload to Supabase storage if we have a workspaceId
        let imagePath = null;

        if (workspaceId) {
          // Create workspace object with only user_id
          const workspaceData = {
            id: workspaceId,
            user_id: currentUser?.id,
          };

          // Use the updated uploadWorkspaceImage function
          imagePath = await uploadWorkspaceImage(workspaceData, file);

          // Check if this is a team workspace and duplicate the image to member paths
          try {
            // Get the workspace to check if it's a team workspace
            const { data: workspace } = await supabase
              .from("workspaces")
              .select("is_team")
              .eq("id", workspaceId)
              .single();

            if (workspace?.is_team) {
              // Get workspace members
              const members = await getWorkspaceMembers(workspaceId);

              if (members.length > 1) {
                // Duplicate the image to each member's path
                await duplicateImageToMemberPaths(file, imagePath, members);
              }
            }
          } catch (error) {
            console.error("Error duplicating image to member paths:", error);
            // Continue with the upload process even if duplication fails
          }
        } else {
          // Generate a temporary path for the file that will be used when workspace is created
          // Format should match the actual upload path format (without avatars/ prefix)
          imagePath = `temp_${currentUser?.id || "unknown"}/workspaces/temp_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
        }

        // Call the onImageChange callback with the file and path
        const imageData = {
          file,
          image_path: imagePath,
          previewUrl,
        };

        onImageChange && onImageChange(imageData);
      } catch (err) {
        console.error("Upload error:", err);
        setError("Failed to upload image: " + (err.message || "Unknown error"));
        setPreview(null);
      } finally {
        setIsUploading(false);
      }
    },
    [onImageChange, workspaceId, currentUser]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive: dropzoneIsDragActive,
  } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
    },
    maxSize: 3 * 1024 * 1024,
    multiple: false,
  });

  // Update dragActive state when dropzone state changes
  useEffect(() => {
    setDragActive(dropzoneIsDragActive);
  }, [dropzoneIsDragActive]);

  // Handle remove image
  const handleRemove = () => {
    setPreview(null);
    onImageChange &&
      onImageChange({ file: null, image_path: null, previewUrl: null });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row items-center gap-5">
        {/* Interactive logo upload area - the only trigger for file selection */}
        <div
          {...getRootProps()}
          className={cn(
            "w-32 h-32 rounded-full overflow-hidden flex items-center justify-center border-2 transition-all duration-200 relative cursor-pointer group",
            dragActive
              ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20 border-dashed ring-2 ring-purple-500/30 ring-offset-2 ring-offset-background"
              : preview
                ? "border-purple-300 dark:border-purple-800 hover:border-purple-500 dark:hover:border-purple-600"
                : "border-gray-200 dark:border-zinc-700 border-dashed bg-gray-50 dark:bg-zinc-800/80 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 hover:border-purple-400 dark:hover:border-purple-700 shadow-sm hover:shadow-md"
          )}
        >
          <input {...getInputProps()} />

          {preview ? (
            <>
              <img
                src={preview}
                alt="Logo preview"
                className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white hover:bg-red-600/90 transition-colors z-10 shadow-sm"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Camera className="h-8 w-8 text-white drop-shadow-md transform scale-90 group-hover:scale-100 transition-transform" />
              </div>
            </>
          ) : isUploading ? (
            <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
              <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin mb-2"></div>
              <span className="text-xs">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 group-hover:text-purple-500 transition-colors">
              <div className="p-4 rounded-full bg-gray-100 dark:bg-zinc-800 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/20 transition-colors mb-2">
                <Upload className="w-6 h-6 group-hover:scale-110 transition-transform" />
              </div>
              <span className="text-xs text-center px-2 font-medium">
                Click to upload
              </span>
            </div>
          )}
        </div>

        {/* Simplified text area */}
        <div className="flex flex-col gap-2 flex-1">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium text-gray-800 dark:text-gray-200">
              Team identity
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1.5">
              {!preview
                ? "Square JPG/PNG/WebP (max 3MB)"
                : "Image will appear as a circle"}
            </span>
          </p>

          {error && (
            <p className="text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/10 p-2 rounded border border-red-100 dark:border-red-900/20 flex items-center gap-1.5">
              <span className="text-red-500 font-bold">!</span>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
